import { useState, useCallback } from "react";
import { Survey, QuestionType, DEFAULT_DATE_TIME_CONFIG } from "@/types/survey";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Smartphone, ExternalLink, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/api";

interface SurveyPreviewProps {
  survey: Survey;
}

// Helper to check if URL is external (can be shown directly) or R2 key (needs signed URL)
const isExternalUrl = (url: string) => url.startsWith('http://') || url.startsWith('https://');

export function SurveyPreview({ survey }: SurveyPreviewProps) {
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const handleOpenPreview = useCallback(async () => {
    // Fetch signed URL for an R2 key
    const fetchSignedUrl = async (r2Key: string): Promise<string> => {
      try {
        const response = await apiRequest("POST", `/api/surveys/${survey.id}/files/signed-url`, { key: r2Key });
        const data = await response.json();
        return data.url || r2Key;
      } catch {
        console.error("Failed to fetch signed URL for:", r2Key);
        return r2Key; // Return original key as fallback
      }
    };

    // Pre-fetch all signed URLs for image choice questions
    const fetchAllImageSignedUrls = async (): Promise<Map<string, string>> => {
      const urlMap = new Map<string, string>();
      const r2Keys: string[] = [];

      // Collect all R2 keys from image choice and heat map questions
      for (const question of survey.questions || []) {
        if (question.type === 'image_choice' && question.imageOptions?.images) {
          for (const image of question.imageOptions.images) {
            if (image.url && !isExternalUrl(image.url) && !r2Keys.includes(image.url)) {
              r2Keys.push(image.url);
            }
          }
        }
        
        // Collect heat_map image keys
        if (question.type === 'heat_map' && question.heatMapConfig?.imageKey) {
          const imageKey = question.heatMapConfig.imageKey;
          if (!isExternalUrl(imageKey) && !r2Keys.includes(imageKey)) {
            r2Keys.push(imageKey);
          }
        }
      }

      // Fetch signed URLs in parallel
      const results = await Promise.all(
        r2Keys.map(async (key) => {
          const signedUrl = await fetchSignedUrl(key);
          return { key, signedUrl };
        })
      );

      // Build the map
      for (const { key, signedUrl } of results) {
        urlMap.set(key, signedUrl);
      }

      return urlMap;
    };
    setIsLoadingPreview(true);
    
    try {
      // Fetch all signed URLs for image choice questions
      const signedUrlMap = await fetchAllImageSignedUrls();
      
      // Helper to get display URL (signed URL or original if external)
      const getDisplayUrl = (url: string): string => {
        if (isExternalUrl(url)) return url;
        return signedUrlMap.get(url) || url;
      };
      
      const questionCount = survey.questions?.length || 0;
    
      // Create a full preview HTML document matching the published survey design
      const previewHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${survey.title || 'Survey Preview'} - Preview</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <script>
          tailwind.config = {
            theme: {
              extend: {
                colors: {
                  primary: 'hsl(262, 83%, 58%)',
                  secondary: 'hsl(15, 75%, 65%)'
                }
              }
            }
          }
        </script>
        <style>
          /* Custom scrollbar */
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 10px; }
          ::-webkit-scrollbar-thumb { background: rgba(147, 51, 234, 0.5); border-radius: 10px; }
          ::-webkit-scrollbar-thumb:hover { background: rgba(147, 51, 234, 0.7); }
          
          /* Preview banner */
          .preview-banner {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: linear-gradient(90deg, #7c3aed 0%, #a855f7 50%, #7c3aed 100%);
            color: white;
            text-align: center;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }
          .preview-banner svg {
            width: 16px;
            height: 16px;
          }
        </style>
      </head>
      <body class="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-6 px-4 sm:py-10" style="padding-top: 60px;">
        <!-- Preview Banner -->
        <div class="preview-banner">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
          </svg>
          Preview Mode - This is how your survey will appear to respondents
        </div>

        <div class="max-w-2xl mx-auto">
          <!-- Survey Header -->
          <div class="mb-6 bg-white/95 backdrop-blur shadow-xl rounded-lg border-0">
            <div class="p-6 pb-4">
              <div class="flex items-start gap-3 mb-2">
                <div class="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <svg class="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h1 class="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                    ${survey.title || 'Untitled Survey'}
                  </h1>
                  ${survey.description ? `
                    <p class="text-gray-600 mt-2 text-sm sm:text-base">
                      ${survey.description}
                    </p>
                  ` : ''}
                </div>
              </div>
            </div>
            <div class="px-6 pb-6 pt-0">
              <div class="flex items-center gap-3">
                <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div class="h-full bg-purple-600 rounded-full" style="width: 0%"></div>
                </div>
                <span class="text-sm text-gray-500 font-medium whitespace-nowrap">0%</span>
              </div>
              <p class="text-xs text-gray-400 mt-2">
                0 of ${questionCount} questions answered
              </p>
            </div>
          </div>

          <!-- Questions -->
          <div class="space-y-4">
            ${(survey.questions || []).map((question, index) => `
              <div class="bg-white/95 backdrop-blur shadow-lg rounded-lg border-0 overflow-hidden">
                <div class="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100 py-4 px-4 sm:px-6">
                  <div class="flex items-start gap-3">
                    <span class="bg-purple-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      ${index + 1}
                    </span>
                    <div class="flex-1 min-w-0">
                      <h3 class="font-medium text-gray-900 text-sm sm:text-base leading-snug">
                        ${question.question}
                      </h3>
                      ${question.required ? '<span class="text-xs text-red-500 mt-1 inline-block">* Required</span>' : ''}
                    </div>
                  </div>
                </div>
                <div class="p-4 sm:p-6">
                  ${renderQuestionInput(question, getDisplayUrl)}
                </div>
              </div>
            `).join('')}
          </div>

          <!-- Submit Section -->
          <div class="mt-8 mb-8 space-y-4">
            <button 
              type="button" 
              class="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 text-lg font-semibold shadow-lg rounded-lg transition-all hover:shadow-xl flex items-center justify-center gap-2"
              onclick="alert('This is a preview. Publish your survey to collect real responses!')"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
              Submit Response
            </button>
            
            <p class="text-center text-purple-300 text-xs">
              ${survey.settings?.anonymous ? 'Your response is anonymous' : 'Your response will be recorded'}
            </p>
          </div>

          <!-- Footer -->
          <div class="text-center pb-8">
            <p class="text-purple-300 text-xs">
              Powered by <span class="font-semibold">SurvAI</span>
            </p>
          </div>
        </div>
      </body>
      </html>
    `;

      // Open in new window
      const previewWindow = window.open('', '_blank', 'width=800,height=700,scrollbars=yes,resizable=yes');
      if (previewWindow) {
        previewWindow.document.write(previewHtml);
        previewWindow.document.close();
      }
    } finally {
      setIsLoadingPreview(false);
    }
  }, [survey]);

  const renderQuestionInput = (question: QuestionType, getDisplayUrl: (url: string) => string) => {
    switch (question.type) {
      case 'multiple_choice':
        return `
          <div class="space-y-2">
            ${(question.options || []).map(option => `
              <label class="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 cursor-pointer transition-all">
                <input type="checkbox" class="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500">
                <span class="text-gray-700 text-sm sm:text-base">${option.text}</span>
              </label>
            `).join('')}
          </div>
        `;
      case 'single_choice':
        return `
          <div class="space-y-2">
            ${(question.options || []).map((option) => `
              <label class="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 cursor-pointer transition-all">
                <input type="radio" name="question_${question.id}" class="w-4 h-4 border-gray-300 text-purple-600 focus:ring-purple-500">
                <span class="text-gray-700 text-sm sm:text-base">${option.text}</span>
              </label>
            `).join('')}
          </div>
        `;
      case 'open_text':
        return `<textarea class="w-full p-3 border border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500 resize-none" rows="4" placeholder="Type your answer here..."></textarea>`;
      case 'likert_scale': {
        const scale = question.scale || { min: 1, max: 5 };
        const labels = scale.labels || [];
        return `
          <div class="space-y-4">
            <div class="flex flex-wrap justify-between gap-2">
              ${Array.from({ length: scale.max - scale.min + 1 }, (_, i) => i + scale.min).map((value, idx) => `
                <label class="flex flex-col items-center space-y-2 cursor-pointer group">
                  <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex items-center justify-center transition-all border-gray-300 text-gray-600 group-hover:border-purple-400">
                    <input type="radio" name="question_${question.id}" value="${value}" class="sr-only">
                    <span class="font-semibold text-sm">${value}</span>
                  </div>
                  ${labels[idx] ? `<span class="text-xs text-gray-500 text-center max-w-16">${labels[idx]}</span>` : ''}
                </label>
              `).join('')}
            </div>
            ${labels.length >= 2 ? `
              <div class="flex justify-between text-xs text-gray-400 px-2">
                <span>${labels[0]}</span>
                <span>${labels[labels.length - 1]}</span>
              </div>
            ` : ''}
          </div>
        `;
      }
      case 'dropdown':
        return `
          <select class="w-full p-3 border border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500 bg-white">
            <option value="">Select an option...</option>
            ${(question.options || []).map(option => `<option value="${option.id}">${option.text}</option>`).join('')}
          </select>
        `;
      case 'ranking':
        return `
          <div class="space-y-2">
            <p class="text-sm text-gray-500 mb-3">Drag items to rank them (1 = highest preference):</p>
            <div class="space-y-2">
              ${(question.options || []).map((option, index) => `
                <div class="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-grab select-none hover:bg-purple-50 hover:border-purple-200 transition-all">
                  <div class="p-1 hover:bg-purple-100 rounded transition-colors">
                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
                    </svg>
                  </div>
                  <span class="w-7 h-7 bg-purple-600 text-white rounded-full text-sm font-semibold flex items-center justify-center flex-shrink-0">
                    ${index + 1}
                  </span>
                  <span class="text-gray-700 flex-1">${option.text}</span>
                </div>
              `).join('')}
            </div>
          </div>
        `;
      case 'radio_grid':
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-20">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row, rowIndex) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="text-center p-2">
                        <input type="radio" name="question_${question.id}_row_${rowIndex}" class="w-4 h-4 text-purple-600 focus:ring-purple-500">
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      case 'checkbox_grid':
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-20">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="text-center p-2">
                        <input type="checkbox" class="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500">
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      case 'dropdown_matrix': {
        const dropdownOptions = question.dropdownMatrixOptions?.shared || ['Option 1', 'Option 2', 'Option 3'];
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-28">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="p-2">
                        <select class="w-full h-8 text-xs border border-gray-200 rounded focus:ring-purple-500 focus:border-purple-500 bg-white">
                          <option>Select...</option>
                          ${dropdownOptions.map(option => `
                            <option value="${option}">${option}</option>
                          `).join('')}
                        </select>
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      }
      case 'text_matrix':
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-24">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="p-2">
                        <input type="text" class="h-8 text-sm w-full border border-gray-200 rounded focus:ring-purple-500 focus:border-purple-500" placeholder="...">
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      case 'numeric_matrix':
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-24">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="p-2">
                        <input type="number" class="h-8 text-sm w-full border border-gray-200 rounded focus:ring-purple-500 focus:border-purple-500" placeholder="0">
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      case 'ranking_matrix':
        return `
          <div class="overflow-x-auto -mx-4 px-4">
            <table class="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th class="text-left p-2 border-b border-gray-200 bg-gray-50"></th>
                  ${(question.columns || []).map(column => `
                    <th class="text-center p-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-700 min-w-20">${column.label}</th>
                  `).join('')}
                </tr>
              </thead>
              <tbody>
                ${(question.rows || []).map((row) => `
                  <tr class="border-b border-gray-100">
                    <td class="p-2 font-medium text-gray-700 bg-gray-50">${row.label}</td>
                    ${(question.columns || []).map(() => `
                      <td class="p-2">
                        <select class="w-16 h-8 text-xs mx-auto border border-gray-200 rounded focus:ring-purple-500 focus:border-purple-500 bg-white">
                          <option>#</option>
                          ${Array.from({ length: (question.rows || []).length }, (_, i) => `
                            <option value="${i + 1}">${i + 1}</option>
                          `).join('')}
                        </select>
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      case 'date_time': {
        const config = question.dateTimeConfig ?? DEFAULT_DATE_TIME_CONFIG;
        const mode = config.mode;
        const inputType = mode === "date" ? "date" : mode === "time" ? "time" : "datetime-local";
        const step = mode === "time" ? (config.minuteStep ?? 1) * 60 : undefined;
        return `
          <input type="${inputType}"${step ? ` step="${step}"` : ''} class="w-full p-3 border border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500">
        `;
      }
      case 'file_upload':
        return `
          <div class="relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200 border-gray-300 hover:border-purple-400 hover:bg-purple-50/50">
            <svg class="h-10 w-10 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p class="text-sm font-medium text-gray-700 mb-1">Click to upload or drag and drop</p>
            <p class="text-xs text-gray-500">PDF, DOC, DOCX, images (max 10MB)</p>
          </div>
        `;
      case 'slider_range': {
        const sliderMin = question.sliderRange?.min || 0;
        const sliderMax = question.sliderRange?.max || 100;
        const sliderStep = question.sliderRange?.step || 1;
        const sliderLabels = question.sliderRange?.labels || [];
        const defaultValue = sliderMin;
        const sliderId = `slider_${question.id}`;
        const valueDisplayId = `value_${question.id}`;
        
        return `
          <div class="space-y-4 px-2">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Value:</span>
              <span id="${valueDisplayId}" class="text-lg font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">${defaultValue}</span>
            </div>
            <input 
              id="${sliderId}"
              type="range" 
              min="${sliderMin}" 
              max="${sliderMax}" 
              step="${sliderStep}"
              value="${defaultValue}" 
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              oninput="document.getElementById('${valueDisplayId}').textContent = this.value"
            >
            <div class="flex justify-between text-xs text-gray-400">
              ${sliderLabels.length > 0 
                ? sliderLabels.map(label => `<span>${label}</span>`).join('')
                : `<span>${sliderMin}</span><span>${Math.round((sliderMin + sliderMax) / 2)}</span><span>${sliderMax}</span>`
              }
            </div>
          </div>
        `;
      }
      case 'rating':
        return `
          <div class="flex gap-1 sm:gap-2">
            ${Array.from({ length: 5 }, () => `
              <button type="button" class="p-1 sm:p-2 transition-all transform hover:scale-110 text-gray-300 hover:text-yellow-400">
                <svg class="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                </svg>
              </button>
            `).join('')}
          </div>
        `;
      case 'video_response': {
        const maxDuration = question.videoConfig?.maxDuration || 60;
        return `
          <div class="space-y-4">
            <!-- Video Preview Area -->
            <div class="relative aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl overflow-hidden border-2 border-slate-600">
              <div class="absolute inset-0 flex flex-col items-center justify-center text-white">
                <div class="w-20 h-20 rounded-full bg-purple-600/30 flex items-center justify-center mb-4">
                  <svg class="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <p class="text-sm text-slate-300 mb-1">Video Response</p>
                <p class="text-xs text-slate-400">Click to start recording</p>
              </div>
            </div>
            
            <!-- Recording Controls -->
            <div class="flex items-center justify-center gap-4">
              <button type="button" class="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full font-medium transition-all shadow-lg">
                <div class="w-3 h-3 rounded-full bg-white animate-pulse"></div>
                Start Recording
              </button>
            </div>
            
            <!-- Info -->
            <div class="flex items-center justify-center gap-4 text-xs text-gray-500">
              <div class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Max ${maxDuration} seconds
              </div>
              ${question.videoConfig?.allowRetake !== false ? `
                <div class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  Retake allowed
                </div>
              ` : ''}
            </div>
          </div>
        `;
      }
      case 'image_choice': {
        const imageOptions = question.imageOptions || { images: [], selectionMode: 'single' };
        const images = imageOptions.images || [];
        const isSingle = imageOptions.selectionMode === 'single';
        const inputType = isSingle ? 'radio' : 'checkbox';
        
        return `
          <div class="space-y-4">
            ${images.length === 0 ? `
              <div class="text-center py-8 text-gray-400">
                <svg class="h-12 w-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                <p class="text-sm">No images configured</p>
              </div>
            ` : `
              <p class="text-sm text-gray-500 mb-3">
                ${isSingle ? 'Select one image:' : 'Select one or more images:'}
              </p>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                ${images.map((image, idx) => {
                  const displayUrl = getDisplayUrl(image.url);
                  return `
                  <label class="relative group cursor-pointer">
                    <input type="${inputType}" name="question_${question.id}" value="${idx}" class="sr-only peer">
                    <div class="border-2 rounded-lg overflow-hidden transition-all peer-checked:border-purple-500 peer-checked:ring-2 peer-checked:ring-purple-200 border-gray-200 hover:border-purple-300 group-hover:shadow-md">
                      <div class="aspect-square bg-gray-100">
                        <img src="${displayUrl}" alt="${image.altText || image.caption || 'Image option'}" class="w-full h-full object-cover" onerror="this.parentElement.innerHTML='<div class=\\'flex items-center justify-center h-full bg-gradient-to-br from-purple-50 to-indigo-100\\'><svg class=\\'h-8 w-8 text-purple-400\\' fill=\\'none\\' stroke=\\'currentColor\\' viewBox=\\'0 0 24 24\\'><path stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'2\\' d=\\'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z\\'></path></svg></div>'">
                      </div>
                      ${image.caption ? `
                        <div class="p-2 bg-white border-t border-gray-100">
                          <p class="text-xs text-gray-700 text-center truncate">${image.caption}</p>
                        </div>
                      ` : ''}
                    </div>
                    <!-- Selection indicator -->
                    <div class="absolute top-2 right-2 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
                      <svg class="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                  </label>
                `}).join('')}
              </div>
            `}
            ${imageOptions.allowNoneOfAbove ? `
              <div class="pt-3 border-t border-gray-100">
                <label class="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 cursor-pointer transition-all">
                  <input type="${inputType}" name="question_${question.id}" value="none" class="w-4 h-4 ${isSingle ? '' : 'rounded'} border-gray-300 text-purple-600 focus:ring-purple-500">
                  <span class="text-gray-700 text-sm">${imageOptions.noneOfAboveLabel || 'None of the above'}</span>
                </label>
              </div>
            ` : ''}
          </div>
        `;
      }
      case 'heat_map': {
        const config = question.heatMapConfig;
        const imageKey = config?.imageKey || '';
        const displayUrl = imageKey ? getDisplayUrl(imageKey) : '';
        const maxClicks = config?.maxClicks ?? 1;
        
        return `
          <div class="space-y-3">
            <p class="text-sm text-gray-500 mb-2">
              Click on the image to indicate your selection${maxClicks > 1 ? ` (up to ${maxClicks} clicks)` : ''}:
            </p>
            ${displayUrl ? `
              <div class="relative inline-block border-2 border-gray-200 rounded-lg overflow-hidden cursor-crosshair" style="touch-action: manipulation;">
                <img 
                  src="${displayUrl}" 
                  alt="Click to indicate areas of interest"
                  class="max-w-full h-auto pointer-events-none"
                  draggable="false"
                  onerror="this.parentElement.innerHTML='<div class=\\'flex items-center justify-center h-48 bg-gray-100 text-gray-400\\'><p class=\\'text-sm\\'>Image unavailable</p></div>'"
                >
                <!-- Preview placeholder for clicks -->
                <div class="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 hover:opacity-100 transition-opacity">
                  <div class="bg-white/80 px-3 py-1 rounded-full text-xs text-gray-600 shadow">
                    Click to add selection
                  </div>
                </div>
              </div>
            ` : `
              <div class="flex flex-col items-center justify-center h-48 bg-gray-100 rounded-lg text-gray-400">
                <svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"></path>
                </svg>
                <p class="text-sm">No image configured</p>
              </div>
            `}
            <p class="text-xs text-gray-400 text-center">
              ${config?.allowDeselect !== false ? 'Click on dots to remove them' : ''}
            </p>
          </div>
        `;
      }
      case 'pick_group_rank': {
        const config = question.pickGroupRankConfig;
        const items = config?.items || [];
        const groups = config?.groups || [];
        const allowRanking = config?.allowRankingWithinGroups ?? false;
        
        return `
          <div class="space-y-4">
            <p class="text-sm text-gray-500 mb-2">
              Drag items into groups${allowRanking ? ' and rank them' : ''}:
            </p>
            
            <!-- Item Pool -->
            <div class="border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 p-3">
              <div class="flex items-center justify-between mb-2 px-1">
                <span class="text-sm font-medium text-gray-600">Items to Sort</span>
                <span class="text-xs text-gray-400">${items.length} items</span>
              </div>
              <div class="space-y-1">
                ${items.map(item => `
                  <div class="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg shadow-sm cursor-grab">
                    <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
                    </svg>
                    <span class="text-sm text-gray-700">${item.label}</span>
                  </div>
                `).join('')}
              </div>
            </div>
            
            <!-- Groups -->
            <div class="grid gap-3 ${groups.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'}">
              ${groups.map(group => `
                <div class="border-2 border-gray-200 rounded-lg bg-white min-h-[100px]">
                  <div class="px-3 py-2 border-b bg-gray-50 font-medium text-sm text-gray-800 flex items-center justify-between">
                    <span>${group.label}</span>
                    <span class="text-xs text-gray-400">0 items</span>
                  </div>
                  <div class="p-2 flex items-center justify-center h-16 text-sm text-gray-400">
                    Drop items here
                  </div>
                </div>
              `).join('')}
            </div>
            
            <p class="text-xs text-gray-400 text-center">
              ${allowRanking 
                ? 'Order within each group represents ranking (top = #1)' 
                : 'Items within groups are not ranked'}
            </p>
          </div>
        `;
      }
      default:
        return `<input type="text" class="w-full p-3 border border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500" placeholder="Enter your response...">`;
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview Placeholder */}
      <div className="bg-muted rounded-lg p-4 text-center min-h-[200px] flex flex-col items-center justify-center">
        <Smartphone className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-2">Live preview</p>
        <p className="text-xs text-muted-foreground mb-4">
          {survey.questions?.length || 0} questions
        </p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleOpenPreview}
          disabled={isLoadingPreview}
          className="text-primary hover:text-primary/80"
        >
          {isLoadingPreview ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <ExternalLink className="h-3 w-3 mr-1" />
              Open Full Preview
            </>
          )}
        </Button>
      </div>

      {/* Survey Summary */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Questions:</span>
          <Badge variant="secondary">{survey.questions?.length || 0}</Badge>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Status:</span>
          <Badge 
            variant={(survey.status || "draft") === "published" ? "default" : "secondary"}
            className={
              (survey.status || "draft") === "published" 
                ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" 
                : (survey.status || "draft") === "draft"
                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                : "bg-muted text-muted-foreground"
            }
          >
            {(survey.status || "draft").charAt(0).toUpperCase() + (survey.status || "draft").slice(1)}
          </Badge>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Anonymous:</span>
          <Badge variant="outline">
            {survey.settings?.anonymous ? "Yes" : "No"}
          </Badge>
        </div>
      </div>

      {/* Question Types Breakdown */}
      <div className="pt-4 border-t border-border">
        <p className="text-sm font-medium text-foreground mb-2">Question Types</p>
        <div className="space-y-1">
          {Object.entries(
            survey.questions.reduce((acc, q) => {
              acc[q.type] = (acc[q.type] || 0) + 1;
              return acc;
            }, {} as Record<string, number>)
          ).map(([type, count]) => (
            <div key={type} className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground capitalize">
                {type.replace("_", " ")}:
              </span>
              <span className="font-medium">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
